<script lang="ts" setup>
import type { TripOrder } from "@/types/trip";
import { computed, ref } from "vue";
import { OrderCancellationStatusMap } from "@/constants/order";
import OrderStatus from "@/components/statuses/OrderStatus.vue";
import ShowMoreList from "@/components/lists/ShowMoreList.vue";
import OmniDialog from "@/components/omni/OmniDialog.vue";
import { useOrder } from "@/composables/useOrder";
import VerticalDotsButton from "@/components/buttons/VerticalDotsButton.vue";

const { getOrderRoute, orderColorList } = useOrder();

const props = withDefaults(
  defineProps<{
    canRemove?: boolean;
    order: TripOrder;
    index: number;
  }>(),
  {
    canRemove: false,
  },
);

const emits = defineEmits<{
  (e: "remove", order: TripOrder): void;
}>();

const removeOrderDialog = ref<boolean>(false);

const isCancelled = computed(() => {
  return OrderCancellationStatusMap.some((o) => o.id === props.order.status);
});

function removeOrder() {
  removeOrderDialog.value = false;
  emits("remove", props.order);
}

function showRemoveDialog() {
  removeOrderDialog.value = true;
}
</script>

<template>
  <div :class="orderColorList[index]" class="order-block" data-qa="order-block">
    <div class="order-block__inner bg-white pa-4">
      <div class="order-block__header d-flex align-center justify-space-between mb-3">
        <div class="d-flex align-center">
          <router-link
            :to="{ name: 'order-view', params: { id: order.id } }"
            class="text-primary font-weight-medium text-decoration-none"
            data-qa="order-number"
            target="_blank"
          >
            Order #{{ order.number }}
          </router-link>
          <order-status v-if="isCancelled" :order="order" class="ml-2"></order-status>
        </div>
        <v-menu v-if="canRemove" offset-y>
          <template #activator="{ props: menuProps }">
            <vertical-dots-button v-bind="menuProps" />
          </template>
          <v-list dense>
            <v-list-item @click="showRemoveDialog">
              <v-list-item-title>Remove order from trip</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
      <div class="d-flex align-center text-grey-darken-1 mb-4">
        REF:
        <show-more-list :items="order.refNumbers" class="ml-1" data-qa="order-ref-number"></show-more-list>
      </div>
      <div class="text-grey-darken-3 mb-4 subheading font-weight-medium" data-qa="order-address-route">
        {{ getOrderRoute(order) }}
      </div>
      <div class="d-flex align-center justify-space-between">
        <router-link
          :to="{ name: 'customer-edit', params: { id: order.customer?.id } }"
          class="text-primary font-weight-medium text-decoration-none"
          data-qa="order-customer"
          target="_blank"
        >
          {{ order.customer?.name }}
        </router-link>
        <div class="d-flex">
          <span class="text-grey-darken-2">Rate:</span>
          <span class="text-grey-darken-3 font-weight-medium ml-1" data-qa="order-rate">{{ order.rate }}$</span>
        </div>
      </div>
    </div>
    <omni-dialog
      v-model="removeOrderDialog"
      :title="'Remove order #' + order.number"
      primary-action-label="Remove"
      width="360"
      @primary-action:click="removeOrder"
    >
      <remove-order-dialog :order="order" />
    </omni-dialog>
  </div>
</template>

<style scoped>
.order-block__inner {
  height: 100%;
}
</style>
